import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import carGurusRealUploadService from '../services/carGurusRealUploadService';
import { FiDownload, FiUploadCloud, FiExternalLink, FiCopy, FiCheckCircle } from 'react-icons/fi';

const CarGurusRealUpload = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedUrl, setFeedUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    setLoading(true);
    const data = await carGurusRealUploadService.fetchVehicles();
    setVehicles(data);
    setLoading(false);
  };

  const generateFeed = async () => {
    setLoading(true);
    const result = await carGurusRealUploadService.generateFeedUrl();
    if (result.success) {
      setFeedUrl(result);
    }
    setLoading(false);
  };

  const downloadCSV = async () => {
    await carGurusRealUploadService.downloadCSV();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">CarGurus ACTUAL Upload Process</h1>
          <p className="text-gray-600 mb-8">How to really get your vehicles on CarGurus</p>

          {/* Current Status */}
          <div className="bg-green-50 border border-green-300 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-bold text-green-900 mb-2">✅ Your Account IS Active!</h2>
            <p className="text-green-800">
              CarGurus doesn't accept direct API uploads. They use these methods instead:
            </p>
          </div>

          {/* Inventory Status */}
          <div className="bg-blue-50 rounded-lg p-4 mb-8">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-blue-600 font-medium">Vehicles Ready</p>
                <p className="text-2xl font-bold text-blue-900">{vehicles.length}</p>
              </div>
              <div className="space-x-4">
                <button
                  onClick={downloadCSV}
                  disabled={loading || vehicles.length === 0}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <FiDownload className="w-4 h-4 mr-2" />
                  Download CSV
                </button>
                <button
                  onClick={generateFeed}
                  disabled={loading || vehicles.length === 0}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <FiUploadCloud className="w-4 h-4 mr-2" />
                  Generate Feed URL
                </button>
              </div>
            </div>
          </div>

          {/* Feed URL Result */}
          {feedUrl && feedUrl.success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
              <h3 className="font-bold text-green-900 mb-2">Feed URL Generated!</h3>
              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="text"
                  value={feedUrl.feedUrl}
                  readOnly
                  className="flex-1 px-3 py-2 border rounded bg-white"
                />
                <button
                  onClick={() => copyToClipboard(feedUrl.feedUrl)}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  {copied ? <FiCheckCircle className="w-4 h-4" /> : <FiCopy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-sm text-green-700">
                Use this URL in CarGurus Dealer Portal to set up automatic daily syncs
              </p>
            </div>
          )}

          {/* Upload Methods */}
          <div className="space-y-6">
            {/* Method 1: Dealer Portal */}
            <div className="border rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3">1</span>
                CarGurus Dealer Portal (Recommended)
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-11">
                <li>
                  Go to CarGurus Dealer Admin:{' '}
                  <a 
                    href="https://wwws.cargurus.com/dealer_admin/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline inline-flex items-center"
                  >
                    https://wwws.cargurus.com/dealer_admin/
                    <FiExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </li>
                <li>Navigate to <strong>Inventory → Inventory Feed</strong></li>
                <li>Choose <strong>"URL Feed"</strong> option</li>
                <li>Paste the feed URL generated above</li>
                <li>Set frequency to <strong>"Daily"</strong></li>
                <li>Click <strong>"Save and Test Feed"</strong></li>
              </ol>
              <div className="mt-4 p-4 bg-yellow-50 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Your dealer login credentials should have been emailed to you when you signed up with CarGurus.
                </p>
              </div>
            </div>

            {/* Method 2: Manual Upload */}
            <div className="border rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3">2</span>
                Manual CSV Upload
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-11">
                <li>Click "Download CSV" button above</li>
                <li>Log into CarGurus Dealer Portal</li>
                <li>Go to <strong>Inventory → Upload Inventory</strong></li>
                <li>Select your CSV file</li>
                <li>Click <strong>"Upload"</strong></li>
              </ol>
            </div>

            {/* Method 3: FTP */}
            <div className="border rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3">3</span>
                FTP Upload (For Automated Systems)
              </h3>
              <div className="ml-11">
                <div className="bg-gray-50 rounded p-4 font-mono text-sm">
                  <p>Host: ftp.cargurus.com</p>
                  <p>Username: paradise-ds</p>
                  <p>Password: [Contact CarGurus Support]</p>
                  <p>Directory: /</p>
                  <p>File Format: CSV</p>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Use an FTP client like FileZilla to upload your CSV file daily
                </p>
              </div>
            </div>
          </div>

          {/* Support Info */}
          <div className="mt-8 bg-gray-100 rounded-lg p-6">
            <h3 className="font-bold text-gray-900 mb-2">Need Help?</h3>
            <p className="text-gray-700 mb-4">
              Your CarGurus account is active! If you need help setting up the feed:
            </p>
            <div className="space-y-2">
              <p className="text-gray-700">
                <strong>Contact:</strong> Stan Franson
              </p>
              <p className="text-gray-700">
                <strong>Email:</strong> support@cargurus.com
              </p>
              <p className="text-gray-700">
                <strong>Phone:</strong> 800-227-4878
              </p>
              <p className="text-gray-700">
                <strong>Tell them:</strong> "I need help setting up my inventory feed URL"
              </p>
            </div>
            
            <div className="mt-4 p-4 bg-white rounded border border-gray-200">
              <p className="text-sm text-gray-600">
                <strong>Your Account Details:</strong><br />
                Dealer Name: Paradise-ds<br />
                App ID: acbbc8e9-fcfd-4bae-ac9a-2e1a2f1b0de2
              </p>
            </div>
          </div>

          {/* The Truth */}
          <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="font-bold text-red-900 mb-2">The Truth About CarGurus Integration</h3>
            <p className="text-red-800">
              CarGurus doesn't have a public REST API for inventory uploads. They only accept:
            </p>
            <ul className="list-disc list-inside mt-2 text-red-700">
              <li>CSV files via their dealer portal</li>
              <li>FTP uploads with credentials they provide</li>
              <li>Feed URLs they can scrape daily</li>
              <li>Integration through DMS providers (DealerSocket, etc.)</li>
            </ul>
            <p className="text-red-800 mt-3">
              The API credentials in your email are for <strong>statistics and reporting only</strong>, not for uploading inventory.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CarGurusRealUpload;
